import { computed, ref, watch } from 'vue'
import type { MultiselectOption, ProjectJson, ProjectListResponse } from '@ironsight/types'
import Project from '@/models/project'
import { useGetProjectsQuery } from '@/api/useProjectsQueries'
import { PaginatedDataProvider } from '@/models/types/paginated-data-provider'

export function useProjectFilterDataProvider(): PaginatedDataProvider {
  const options = ref<MultiselectOption[]>([])
  const offset = ref(0)
  const currentQueryText = ref('')

  const queryGenerator = computed(() => {
    return {
      offset: offset.value ?? 0,
      limit: 50,
      query: currentQueryText.value,
    }
  })

  const { data: projects, isLoading, isFetching } = useGetProjectsQuery(queryGenerator)

  watch(
    projects,
    (newProjects: ProjectListResponse | undefined) => {
      if (newProjects) {
        const newOptions = newProjects.data.map((b: ProjectJson) => {
          const project = new Project(b)
          return {
            label: `${project.displayName}`,
            value: project.id,
          }
        })
        const existingIds = new Set(options.value.map((item) => item.value))
        const uniqueNewOptions = newOptions.filter((opt) => !existingIds.has(opt.value))

        options.value.push(...uniqueNewOptions)
      }
    },
    { immediate: true },
  )

  const hasMore = computed<boolean>(() => {
    const hasNoMetadata = !projects?.value?.meta
    const hasNoTotalPages = !projects?.value?.meta?.totalPages

    if (hasNoMetadata || hasNoTotalPages) {
      return true
    }

    return Boolean(
      projects?.value?.meta?.totalPages &&
      projects?.value?.meta?.currentPage < projects?.value?.meta?.totalPages,
    )
  })

  function fetchNextPage(queryText: string = currentQueryText.value) {
    if (isLoading.value && queryText === currentQueryText.value) {
      return
    }
    if (isFetching.value && queryText === currentQueryText.value) {
      return
    }
    if (!hasMore.value && queryText === currentQueryText.value) {
      return
    }

    if (queryText !== currentQueryText.value) {
      currentQueryText.value = queryText
      offset.value = 0
      options.value = []
    }

    offset.value = options.value.length
  }

  return {
    options,
    isLoading: computed(() => isLoading.value),
    isFetching: computed(() => isFetching.value),
    hasMore: computed(() => hasMore.value),
    fetchNextPage,
  }
}
